// Meta61 — editorial content (single source of truth).
// Australian English spelling throughout. Copy blocks are production-ready,
// drawn from the Meta61 Website Content Strategy & Build Brief.

export const SITE = {
  name: "Meta61",
  tagline: "The convergence company",
  positioningLine: "Where technology and governance converge.",
  contactEmail: "info@meta61.com.au",
  longDescription:
    "Meta61 is an Australian think tank and advisory at the convergence of artificial intelligence and emerging technology — engineering systems that are secure, sustainable, and sovereign.",
};

// Primary navigation + Contact CTA. Logo links Home.
export const NAV_LINKS = [
  { to: "/about", label: "About" },
  { to: "/focus", label: "Focus" },
  { to: "/frameworks", label: "Frameworks" },
  { to: "/impact", label: "Impact" },
  { to: "/insights", label: "Insights" },
  { to: "/resources", label: "Resources" },
];

// Home — three difference pillars.
export const HOME_PILLARS = [
  {
    title: "Technology, mastered.",
    body: "AI as the cornerstone, surrounded by the converging stack.",
    icon: "cpu",
  },
  {
    title: "Governance, embedded.",
    body: "Standards, policy, ethics, and assurance built in — not bolted on.",
    icon: "scale",
  },
  {
    title: "Sovereignty, protected.",
    body: "Australian capability first; global standards by contribution.",
    icon: "shield",
  },
];

// The convergence stack — AI cornerstone + orbiting technologies.
export const STACK = [
  {
    name: "Artificial Intelligence",
    tag: "Cornerstone",
    icon: "brain",
    short:
      "From applied machine learning to generative and agentic systems, AI is the centre of gravity for the modern enterprise and the modern state.",
    governance:
      "Lifecycle assurance, human oversight, and management systems aligned to ISO/IEC 42001, the NIST AI RMF, and the OECD AI Principles.",
  },
  {
    name: "Cybersecurity & digital trust",
    icon: "lock",
    short:
      "Security is the precondition for everything else. We treat resilience, integrity, and assurance as first-class design goals.",
    governance: "Secure-by-design, critical-infrastructure protection, and supply-chain integrity.",
  },
  {
    name: "Immersive & spatial computing",
    icon: "glasses",
    short:
      "The metaverse, XR, AR/VR, and digital twins are creating a spatial layer over the physical world.",
    governance: "Identity, safety, data rights, and trust in immersive environments.",
  },
  {
    name: "Blockchain & decentralised trust",
    icon: "link",
    short:
      "Distributed ledgers and Web3 provide verifiable provenance and trust infrastructure for data and transactions.",
    governance: "Transparency, provenance, and accountable decentralisation.",
  },
  {
    name: "Robotics & autonomous systems",
    icon: "bot",
    short:
      "Physical autonomy is moving from the lab to the field, industry, and defence.",
    governance:
      "Meaningful human control, fail-safe design, and — where relevant — international humanitarian law.",
  },
  {
    name: "Remote sensing & Earth observation",
    icon: "satellite",
    short:
      "Space-based and sensing technologies underpin climate, disaster, agriculture, and national-capability use cases.",
    governance: "Data sovereignty, dual-use safeguards, and environmental application.",
  },
  {
    name: "Smart cities, IoT & sensor networks",
    icon: "network",
    short:
      "Instrumented environments generate continuous data at population scale.",
    governance: "Privacy, consent, and equitable, resilient urban systems.",
  },
  {
    name: "Quantum technologies",
    tag: "Emerging",
    icon: "atom",
    short:
      "Quantum computing and sensing will reshape security and capability over the coming decade.",
    governance: "Post-quantum cybersecurity readiness and responsible-adoption pathways.",
  },
];

export const MANDATES = [
  {
    title: "Australian sovereignty & critical standards",
    body: "We strengthen national capability and critical-technology resilience, and help build the trusted standards Australia depends on.",
  },
  {
    title: "Global standards, policy & sustainability",
    body: "We contribute to the frameworks of the world's peak bodies — and help Australia shape them, not just adopt them.",
  },
];

export const OPERATING_MODELS = [
  {
    name: "KITE — digital & AI orchestration",
    body: "Our abstraction framework for orchestrating transformation toward resilience, sustainability, growth, and prosperity.",
    to: "/focus",
  },
  {
    name: "The Wind Turbine — sovereign capability",
    body: "Our model for turning standards, values, and partnerships into trusted, sovereign outcomes.",
    to: "/frameworks",
  },
];

// Trusted engagements logo wall. Files live in /public/logos.
export const LOGOS = [
  { file: "1.png", name: "Australian Government — Department of Industry, Science and Resources" },
  { file: "2.png", name: "Australian Computer Society (ACS)" },
  { file: "3.png", name: "IEEE" },
  { file: "4.png", name: "OECD.AI Policy Observatory" },
  { file: "5.png", name: "Harvard Business Review" },
  { file: "6.png", name: "WWF" },
  { file: "7.png", name: "IFRC — Red Cross and Red Crescent" },
  { file: "8.png", name: "UN Sustainable Development Goals" },
  { file: "9.png", name: "The Teachers' Guild of NSW" },
  { file: "10.png", name: "Global AI Ethics Institute" },
  { file: "11.png", name: "Women in AI" },
  { file: "12.png", name: "The Fair — the festival of AI and robotics" },
  { file: "13.png", name: "Boston University" },
  { file: "14.png", name: "The University of Sydney" },
  { file: "15.png", name: "University of Technology Sydney (UTS)" },
  { file: "16.png", name: "Western Sydney University" },
  { file: "17.png", name: "UNSW Sydney" },
  { file: "18.png", name: "The University of Melbourne" },
  { file: "19.png", name: "Meta61" },
  { file: "20.png", name: "iFusion" },
  { file: "21.svg", name: "Trusted engagement" },
  { file: "22.jpg", name: "IEEE Brain" },
];

export const LOGO_DISCLAIMER =
  "These represent trusted engagements in our capability space for advancing digital and AI transformation — not formal partnerships.";

// About
export const ABOUT = {
  whoWeAre:
    "Meta61 exists in the space most organisations treat as two: technology and governance. We treat them as one. From a base in Australia, we work with enterprises, governments, and the world's peak bodies to orchestrate digital and AI transformation that strengthens sovereignty, advances sustainability, and earns trust by design.",
  purpose:
    "As AI and emerging technology reshape every institution, our purpose is to ensure that what is most powerful is also most trusted — so that technology advances human dignity, sustainability, and the sovereign capability of nations and communities.",
  vision:
    "A world in which every nation, enterprise, and community can thrive in the age of AI — securely, sustainably, and sovereignly — where the convergence of technologies serves human and planetary flourishing rather than diminishing it.",
  mission:
    "We orchestrate digital and AI transformation that is secure, sustainable, and sovereign — uniting deep technical capability with standards-grade governance, and helping enterprises, governments, and peak bodies build, adopt, and govern technology the world can trust. Our KITE and Wind Turbine operating models guide the why, the how, and the what.",
  holdTogether:
    "These commitments are interdependent by design. Dignity without sustainability is short-sighted; sovereignty without inclusion is brittle; capability without integrity is dangerous. We hold them as one — which is why our work is judged not only by what a technology can do, but by whom it serves and what it leaves behind.",
};

export const VALUES = [
  {
    title: "Human dignity first.",
    body: "Technology must serve people, never the reverse. Human rights, agency, and wellbeing are the measure of every system we touch.",
  },
  {
    title: "Interdependence.",
    body: "Technology and governance, organisation and society, capability and conscience are not separable. We see — and design for — the whole.",
  },
  {
    title: "Integrity and accountability.",
    body: "We say what is true, govern what we build, and stand behind our work. Trust is earned, not asserted.",
  },
  {
    title: "Sovereignty without isolation.",
    body: "We strengthen national and organisational independence while contributing openly to global standards and the common good.",
  },
  {
    title: "Sustainability and stewardship.",
    body: "We design for the long term — for the planet, for future generations, and for the resilience of the systems and institutions we serve.",
  },
  {
    title: "Equity and inclusion.",
    body: "Fairness is a design requirement. We bring diversity, equity, and inclusion into technology to advance social justice and reduce harm.",
  },
];

// All 17 UN SDGs (official palette + titles). `key` marks those most relevant to Meta61,
// with a short rationale in `body`.
export const SDGS = [
  { num: 1, title: "No Poverty", colour: "#E5243B" },
  { num: 2, title: "Zero Hunger", colour: "#DDA63A" },
  { num: 3, title: "Good Health & Well-being", colour: "#4C9F38" },
  { num: 4, title: "Quality Education", colour: "#C5192D", key: true, body: "Building national and organisational capability for the age of AI." },
  { num: 5, title: "Gender Equality", colour: "#FF3A21", key: true, body: "Diversity, equity, and inclusion in technology — in the spirit of our work with Women in AI." },
  { num: 6, title: "Clean Water & Sanitation", colour: "#26BDE2" },
  { num: 7, title: "Affordable & Clean Energy", colour: "#FCC30B", key: true, body: "Distributed, low-energy AI architectures that reduce the energy footprint of technology." },
  { num: 8, title: "Decent Work & Economic Growth", colour: "#A21942", key: true, body: "Orchestrating transformation toward growth and prosperity that is sustainable and broadly shared." },
  { num: 9, title: "Industry, Innovation & Infrastructure", colour: "#FD6925", key: true, body: "AI and the converging technology stack as resilient, trustworthy infrastructure for enterprises and nations." },
  { num: 10, title: "Reduced Inequalities", colour: "#DD1367", key: true, body: "DEI for social justice in AI — embedding fairness and reducing algorithmic harm and bias." },
  { num: 11, title: "Sustainable Cities & Communities", colour: "#FD9D24", key: true, body: "Smart cities, IoT, and sensor networks designed for privacy, equity, and urban resilience." },
  { num: 12, title: "Responsible Consumption & Production", colour: "#BF8B2E" },
  { num: 13, title: "Climate Action", colour: "#3F7E44", key: true, body: "Applying AI, sensing, and data to climate action and disaster resilience." },
  { num: 14, title: "Life Below Water", colour: "#0A97D9" },
  { num: 15, title: "Life on Land", colour: "#56C02B" },
  { num: 16, title: "Peace, Justice & Strong Institutions", colour: "#00689D", key: true, body: "Governance, accountability, and the rule of law applied to AI and autonomous systems — including IHL." },
  { num: 17, title: "Partnerships for the Goals", colour: "#19486A", key: true, body: "Contributing to the world's peak bodies and standards processes, and helping Australia shape them." },
];

export const FOCUS_AUDIENCES = [
  {
    title: "Enterprises",
    body: "Adopting AI and emerging technology at scale, who need both capability and trust.",
  },
  {
    title: "Governments & public institutions",
    body: "Building national capability, critical standards, and sovereign resilience.",
  },
  {
    title: "Peak & standards bodies",
    body: "Developing the frameworks, policies, and standards that govern technology globally.",
  },
];

// Frameworks
export const SOVEREIGNTY_POINTS = [
  "National AI capability and critical-technology resilience",
  "Data, compute, and infrastructure sovereignty",
  "Trusted standards, assurance, and conformity",
  "Alignment of Australian practice with interoperable global frameworks",
];

export const GOVERNANCE_DOMAINS = [
  {
    title: "AI governance & standards",
    body: "Trustworthy-AI principles, management systems, and risk frameworks — including the OECD AI Principles, the UNESCO Recommendation on the Ethics of AI, ISO/IEC 42001, the IEEE 7000 series, and the NIST AI Risk Management Framework.",
  },
  {
    title: "Sustainability & the SDGs",
    body: "Aligning technology with the UN Sustainable Development Goals, environmental sustainability, and responsible innovation.",
  },
  {
    title: "Climate & disaster resilience",
    body: "Applying AI, sensing, and data to climate action and disaster resilience — in the spirit of work with WWF and the IFRC.",
  },
  {
    title: "International humanitarian law & autonomous systems",
    body: "Contributing to the governance of autonomous weapon systems and the application of IHL to emerging military technology, alongside the work of the ICRC and the UN CCW process.",
  },
  {
    title: "Cybersecurity & digital trust",
    body: "Security, integrity, and resilience as the foundation of trustworthy systems.",
  },
  {
    title: "Responsible-AI assurance & auditing",
    body: "Independent assessment and assurance against recognised standards.",
  },
];

// Impact — verified publications.
export const PUBLICATIONS = [
  {
    category: "AI Governance and Ethics",
    items: [
      { text: "Samarawickrama, M. (2022). AI governance and ethics framework for sustainable AI and sustainability — submission to the Department of the Prime Minister and Cabinet (Automated Decision Making and AI Regulation). ISBN 978-0-6454693-0-1.", url: "https://doi.org/10.48550/arXiv.2210.08984" },
      { text: "Samarawickrama, M. (2021). KITE: An abstraction framework for reducing complexity in AI governance. UN World Data Forum.", url: "https://unstats.un.org/unsd/undataforum/blog/KITE-an-abstraction-framework-for-reducing-complexity-in-ai-governance" },
      { text: "Samarawickrama, M. (2022). Keeping AI honest. Company Director, 38(2), 52\u201353.", url: "https://www.aicd.com.au/organisational-culture/business-ethics/issues/keeping-AI-honest.html" },
      { text: "Samarawickrama, M. (2022). Importance of an AI governance toolkit for environmental, social and governance. Global Trends in AI 2022 (GAIEI).", url: "https://globalethics.ai/global-trends-in-ai-2022-food-for-thought-from-gaiei-experts/" },
      { text: "Samarawickrama, M. (2023). A conceptual design of an AI-enabled decision support system for analysing donor behaviour in nonprofit organisations. Information, 14(10), 578.", url: "https://doi.org/10.3390/info14100578" },
      { text: "Samarawickrama, M. (2022). Analysing donors' behaviours in nonprofit organisations: A design science research framework. In Proceedings of the 2022 Conference (pp. 642\u2013654).", url: "https://link.springer.com/chapter/10.1007/978-981-19-1520-8_62" },
      { text: "Samarawickrama, M. (2022). Analysing donors' behaviour in nonprofit organisations for disaster resilience: The 2019\u20132020 Australian bushfires case study.", url: "https://doi.org/10.48550/arXiv.2210.09034" },
      { text: "Samarawickrama, M. (2022). The effectiveness of social media engagement strategy on disaster fundraising.", url: "https://doi.org/10.48550/arXiv.2210.11322" },
    ],
  },
  {
    category: "Emerging Technologies and Innovations",
    items: [
      { text: "Samarawickrama, M. (2024). Transforming tourism loyalty programmes through private blockchain. In 2024 IEEE Conference on Engineering Informatics (ICEI) (pp. 1\u201311).", url: "https://doi.org/10.1109/ICEI64305.2024.10912187" },
    ],
  },
  {
    category: "Commentary & policy",
    items: [
      { text: "Samarawickrama, M. (2026, May 4). Invisible infrastructure. Government News. Argues that AI sovereignty depends on standards and governance leadership (\u201cinvisible infrastructure\u201d), not compute alone, and sets out a playbook for middle powers such as Australia.", url: "https://www.governmentnews.com.au/type_contributors/invisible-infrastructure/" },
    ],
  },
];

export const IMPACT_FORMS = [
  "Standards & framework development",
  "Advisory & assurance",
  "Applied research",
  "Thought leadership",
];

// Insights
export const FEATURED_INSIGHT = {
  eyebrow: "Featured · AI sovereignty",
  title: "Invisible infrastructure",
  source: "Government News",
  date: "4 May 2026",
  theme: "Sovereignty",
  summary:
    "AI sovereignty is usually framed around the visible assets — compute, data centres, cloud, domestic models. This piece argues the decisive layer is invisible: the standards and governance leadership that determine interoperability, accountability, and whether a nation remains a rule-maker rather than a rule-taker. It sets out where middle powers like Australia hold genuine comparative advantage — trusted governance, AI assurance, cybersecurity, responsible innovation, Indigenous Data Sovereignty, and regional cooperation — and a practical playbook to turn those strengths into standards leadership.",
  url: "https://www.governmentnews.com.au/type_contributors/invisible-infrastructure/",
};

export const INSIGHT_THEMES = [
  "AI Governance",
  "Sovereignty",
  "Sustainability & SDGs",
  "IHL & Autonomous Systems",
  "Convergence Technology",
];

export const INSIGHTS = [
  {
    title: "Keeping AI honest",
    theme: "AI Governance",
    date: "2022",
    summary: "On the board's role in governing AI with integrity and accountability — published in Company Director (AICD).",
    url: "https://www.aicd.com.au/organisational-culture/business-ethics/issues/keeping-AI-honest.html",
  },
  {
    title: "KITE: reducing complexity in AI governance",
    theme: "AI Governance",
    date: "2021",
    summary: "An abstraction framework introduced at the UN World Data Forum for orchestrating AI governance toward the public good.",
    url: "https://unstats.un.org/unsd/undataforum/blog/KITE-an-abstraction-framework-for-reducing-complexity-in-ai-governance",
  },
  {
    title: "An AI governance toolkit for ESG",
    theme: "Sustainability & SDGs",
    date: "2022",
    summary: "Why environmental, social, and governance outcomes depend on operationalising AI governance — featured in Global Trends in AI.",
    url: "https://globalethics.ai/global-trends-in-ai-2022-food-for-thought-from-gaiei-experts/",
  },
  {
    title: "Private blockchain for tourism loyalty",
    theme: "Convergence Technology",
    date: "2024",
    summary: "Applying decentralised trust infrastructure to loyalty programmes — IEEE Conference on Engineering Informatics (ICEI).",
    url: "https://doi.org/10.1109/ICEI64305.2024.10912187",
  },
];

// Resources — curated open library.
export const RESOURCE_GROUPS = [
  {
    heading: "AI governance, ethics & standards",
    items: [
      { name: "OECD AI Principles", desc: "The first intergovernmental standard on AI (adopted 2019, updated 2024).", url: "https://oecd.ai/en/ai-principles" },
      { name: "OECD.AI Policy Observatory", desc: "A live global database of AI policies, tools, and metrics.", url: "https://oecd.ai/" },
      { name: "UNESCO Recommendation on the Ethics of AI", desc: "The first global standard on AI ethics, adopted by 193 member states (2021).", url: "https://www.unesco.org/en/artificial-intelligence/recommendation-ethics" },
      { name: "ISO/IEC 42001:2023", desc: "The world's first AI management system (AIMS) standard.", url: "https://www.iso.org/standard/42001" },
      { name: "NIST AI Risk Management Framework", desc: "A voluntary framework structured around Govern, Map, Measure, Manage.", url: "https://www.nist.gov/itl/ai-risk-management-framework" },
      { name: "IEEE Global Initiative on Ethics of A/IS", desc: "Ethically Aligned Design and the IEEE 7000 series of standards.", url: "https://standards.ieee.org/industry-connections/activities/ieee-global-initiative/" },
    ],
  },
  {
    heading: "Australian sovereignty & critical standards",
    items: [
      { name: "Australia's AI Ethics Principles", desc: "Eight voluntary principles from the Department of Industry, Science and Resources.", url: "https://www.industry.gov.au/publications/australias-ai-ethics-principles" },
      { name: "Voluntary AI Safety Standard", desc: "Ten guardrails for safe and responsible AI across the supply chain.", url: "https://www.industry.gov.au/publications/voluntary-ai-safety-standard" },
      { name: "Guidance for AI Adoption", desc: "The National AI Centre's operational guidance evolving the principles and guardrails.", url: "https://www.industry.gov.au/publications/australias-ai-ethics-principles" },
    ],
  },
  {
    heading: "Sustainability & the SDGs",
    items: [
      { name: "UN Sustainable Development Goals", desc: "The 17 global goals for people, planet, and prosperity.", url: "https://sdgs.un.org/goals" },
    ],
  },
  {
    heading: "International humanitarian law & autonomous systems",
    items: [
      { name: "ICRC — Autonomous weapons", desc: "The ICRC's position and law-and-policy hub on autonomous weapon systems and IHL.", url: "https://www.icrc.org/en/law-and-policy/autonomous-weapons" },
    ],
  },
  {
    heading: "Meta61 operating models",
    items: [
      { name: "KITE — abstraction framework for AI governance", desc: "Meta61's orchestration model, introduced at the UN World Data Forum.", url: "https://unstats.un.org/unsd/undataforum/blog/KITE-an-abstraction-framework-for-reducing-complexity-in-ai-governance" },
      { name: "AI Governance & Ethics Framework for Sustainable AI", desc: "The paper presenting both the KITE and Wind Turbine models.", url: "https://doi.org/10.48550/arXiv.2210.08984" },
    ],
  },
];

export const RESOURCE_DISCLAIMER =
  "These external resources are provided for reference. Meta61 is independent of these organisations unless explicitly stated.";

// Contact — area of interest options.
export const INTEREST_OPTIONS = [
  "AI Governance & Standards",
  "Sovereignty & Critical Technology",
  "Sustainability & SDGs",
  "IHL & Autonomous Systems",
  "Convergence Technology Advisory",
  "Other",
];

// Footer
export const FOOTER_NAV = [
  {
    heading: "Explore",
    links: [
      { to: "/about", label: "About" },
      { to: "/focus", label: "Focus" },
      { to: "/frameworks", label: "Frameworks" },
      { to: "/impact", label: "Impact" },
      { to: "/insights", label: "Insights" },
      { to: "/resources", label: "Resources" },
      { to: "/contact", label: "Contact" },
    ],
  },
  {
    heading: "Engage",
    links: [
      { to: "/resources#assessment", label: "Convergence Readiness Assessment" },
      { to: "/privacy", label: "Privacy" },
      { to: "/terms", label: "Terms" },
    ],
  },
];

// Readiness Assessment — five dimensions.
export const ASSESSMENT = {
  levels: ["Ad hoc", "Developing", "Defined", "Managed", "Optimised"],
  dimensions: [
    {
      key: "strategy",
      label: "Strategy & Sovereignty",
      maps: "OECD AI Principles; Australian critical-tech & sovereignty",
      questions: [
        "How clearly is your technology strategy tied to your organisation's independence over critical data, compute, and capability?",
        "How well do you understand and manage your dependency on critical technologies and their supply chains?",
      ],
      recommendations: {
        low: "Define a technology strategy that names your critical data, compute, and capability dependencies. Use the OECD AI Principles to frame independent, accountable choices.",
        high: "Stress-test your sovereignty posture against supply-chain and geopolitical scenarios, and contribute your practice back to interoperable standards.",
      },
    },
    {
      key: "governance",
      label: "Governance & Accountability",
      maps: "ISO/IEC 42001; Australia's Guidance for AI Adoption",
      questions: [
        "To what extent do you have defined accountability, policies, and oversight for the AI systems you build or use?",
        "How embedded is board and executive oversight of AI in your management system?",
      ],
      recommendations: {
        low: "Establish clear accountability and an AI policy. Adopt an AI management system aligned to ISO/IEC 42001 and the National AI Centre's Guidance for AI Adoption.",
        high: "Mature your management system with continual improvement, internal audit, and board-level reporting against recognised standards.",
      },
    },
    {
      key: "risk",
      label: "Risk, Safety & Assurance",
      maps: "NIST AI RMF; Voluntary AI Safety Standard guardrails",
      questions: [
        "How systematically do you identify, test for, and monitor the risks your AI systems pose across their lifecycle?",
        "How prepared are you to detect and respond to AI incidents?",
      ],
      recommendations: {
        low: "Adopt the NIST AI RMF (Govern, Map, Measure, Manage) and the Voluntary AI Safety Standard guardrails to structure testing, monitoring, and incident response.",
        high: "Introduce independent assurance and red-teaming, and align incident response with critical-infrastructure obligations.",
      },
    },
    {
      key: "trust",
      label: "Trust & Ethics",
      maps: "UNESCO Recommendation; Australia's AI Ethics Principles; IEEE 7000",
      questions: [
        "How well can the people affected by your AI systems understand, contest, and rely on the decisions those systems make?",
        "How effectively do you assess fairness, transparency, and human oversight before deployment?",
      ],
      recommendations: {
        low: "Embed transparency, human oversight, and contestability using Australia's AI Ethics Principles and the IEEE 7000 series; align with the UNESCO Recommendation.",
        high: "Operationalise fairness and human-rights impact assessment, and publish transparency artefacts for high-impact systems.",
      },
    },
    {
      key: "sustainability",
      label: "Sustainability & Resilience",
      maps: "UN SDGs; sustainability & resilience practice",
      questions: [
        "To what extent do you assess the environmental footprint and long-term resilience of your technology choices?",
        "How well is your technology aligned to sustainability goals and climate/disaster resilience?",
      ],
      recommendations: {
        low: "Measure the environmental footprint of your AI and map initiatives to the UN SDGs; favour distributed, resilient architectures.",
        high: "Set science-based targets for technology sustainability and build climate and disaster resilience into your roadmap.",
      },
    },
  ],
  tiers: [
    { name: "Initiating", min: 1.0, max: 1.99, blurb: "Early and ad hoc. Foundations for trustworthy technology are not yet in place — a clear opportunity to build deliberate strategy, governance, and assurance." },
    { name: "Developing", min: 2.0, max: 2.99, blurb: "Practices are forming but uneven. Strengthen consistency, accountability, and the link between capability and governance." },
    { name: "Established", min: 3.0, max: 3.99, blurb: "Defined and managed across most dimensions. Focus on assurance, continual improvement, and contributing to standards." },
    { name: "Leading", min: 4.0, max: 5.0, blurb: "Optimised and standards-grade. You are positioned to shape practice and standards, not just adopt them." },
  ],
};
